import React, { forwardRef, useEffect, useRef, useState } from 'react';
import _classNames from 'classnames';
import Navigation from 'swiper/modules/navigation/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowLeft from 'components/Carousel/images/arrow-left.svg';
import ArrowRight from 'components/Carousel/images/arrow-right.svg';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(() => ({
  swiperContainer: props => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
    touchAction: 'none',

    '& .swiper-wrapper': {
      display: 'flex',
      padding: 0,
      margin: 0,
      ...(props.small && {
        marginTop: '-20px',
        marginBottom: '-32px',
        paddingTop: '20px',
        paddingBottom: '32px',
      }),
    },
  }),
  swiperSlide: props => {
    if (!props.dynamicSlidesIndexes) {
      return {
        flexShrink: 0,
        padding: '0 12px',
        listStyle: 'none',
        width: props.slideWidth,
      };
    }

    const nth = props.dynamicSlidesIndexes
      .map(s => `&:nth-child(${s + 1})`)
      .join(',');

    return {
      flexShrink: 0,
      padding: '0 12px',
      listStyle: 'none',
      [nth]: {
        width: 'auto',
      },
      width: props.slideWidth,
    };
  },
  arrow: {
    position: 'absolute',
    zIndex: 1,
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    background: 'transparent',
    '& > img': {
      height: 32,
    },
    '&:disabled': {
      visibility: 'hidden',
    },
    '&.right': {
      right: props => (props.small ? '-16px' : '-30px'),
    },
    '&.left': {
      left: props => (props.small ? '-16px' : '-30px'),
    },
  },
}));

export default forwardRef(function SwiperCarousel(
  {
    speed = 400,
    small = true,
    keyboard = true,
    arrows = true,
    slidesPerView,
    breakpoints,
    dynamicSlidesIndexes,
    onRightArrowClick,
    onLeftArrowClick,
    children,
    ...rest
  },
  ref,
) {
  const [slideWidth, setSlideWidth] = useState();
  const classes = useStyles({
    small,
    dynamicSlidesIndexes,
    slideWidth,
  });
  const swiperRef = useRef(null);

  useEffect(() => {
    if (slideWidth !== undefined && swiperRef.current) {
      swiperRef.current.update();
    }
  }, [slideWidth]);

  const onBreakpoint = (swiper, breakpointOptions) => {
    if (breakpointOptions.slidesPerView !== 'auto') {
      return;
    }

    const width = Math.round(
      swiper.el.clientWidth / breakpointOptions.slidesPerGroup,
    );
    setSlideWidth(width);
  };

  const setRefs = node => {
    swiperRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else {
      // eslint-disable-next-line no-param-reassign
      ref.current = node;
    }
  };

  const onInit = swiper => {
    setRefs(swiper);
  };

  return (
    <Swiper
      onInit={onInit}
      className={classes.swiperContainer}
      wrapperTag="ul"
      slidesPerView={slidesPerView}
      onBreakpoint={onBreakpoint}
      // onAfterInit={onAfterInit}
      breakpoints={
        breakpoints || {
          1024: {
            slidesPerView: 3.8,
            slidesPerGroup: 4,
          },
          464: {
            slidesPerView: 2.8,
            slidesPerGroup: 3,
          },
          0: {
            slidesPerView: 1.8,
            slidesPerGroup: 2,
          },
        }
      }
      modules={arrows ? [Navigation] : undefined}
      navigation={{
        nextEl: '#next-arrow',
        prevEl: '#prev-arrow',
      }}
      keyboard={keyboard}
      speed={speed}
      {...rest}
    >
      {React.Children.map(children, child => (
        <SwiperSlide tag="li" className={classes.swiperSlide}>
          {child}
        </SwiperSlide>
      ))}
      {arrows && (
        <>
          <button
            type="button"
            id="next-arrow"
            onClick={onRightArrowClick}
            className={_classNames(classes.arrow, 'right')}
          >
            <img src={ArrowRight} alt="Arrow Right" />
          </button>
          <button
            type="button"
            id="prev-arrow"
            onClick={onLeftArrowClick}
            className={_classNames(classes.arrow, 'left')}
          >
            <img src={ArrowLeft} alt="Arrow Left" />
          </button>
        </>
      )}
    </Swiper>
  );
});
